/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { jsx } from '@atlaskit/css';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	createSkillNode,
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { type Skill } from '@atlassian/rovo-platform-types/skills';
import { resolveSkillDisplayDescription } from '@atlassian/rovo-platform-ui-components/skills/resolve-skill-display-description';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { BUILD_SKILL_ARI } from '@atlassian/rovo-skills/build-skill-probe';
import { buildSkillsManifest } from '@atlassian/rovo-skills/build-skills-manifest';
import { useCustomSkillsAnalytics } from '@atlassian/skills-utils/use-custom-skills-analytics';

import { useStableEventCallback } from '../../common/hooks/useStableEventCallback';
import { useSearchMode } from '../chat-prompt-input/useSearchMode';
import { createSkillTagNodeRenderer } from './createSkillTagNodeRenderer';
import { isSpecialistAgentSelected } from './isSpecialistAgentSelected';
import { useSlashCommandSkillEligibility } from './useSlashCommandSkillEligibility';

const getLegacySkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

const SKILL_DESCRIPTION_CHAR_LIMIT = 150;
const EMPTY_SKILLS: Skill[] = [];

const truncateSkillDescription = (description: string | null | undefined): string => {
	if (!description) {
		return '';
	}
	if (description.length <= SKILL_DESCRIPTION_CHAR_LIMIT) {
		return description;
	}
	const slice = description.slice(0, SKILL_DESCRIPTION_CHAR_LIMIT);
	const lastWhitespace = slice.search(/\s\S*$/);

	const truncated = lastWhitespace > 0 ? slice.slice(0, lastWhitespace) : slice;
	return `${truncated.trimEnd()}…`;
};

const skillsEditorMessages = defineMessages({
	betaLabel: {
		id: 'conversation-assistant-chat-prompt-input.skills-editor-extension.beta-label',
		defaultMessage: 'Beta',
		description: 'Beta badge label shown next to admin skill actions in the slash command menu',
	},
});

const adminSkillBetaLozenge = (
	<Lozenge appearance="discovery">
		<FormattedMessage {...skillsEditorMessages.betaLabel} />
	</Lozenge>
);

type UseSkillsManifestProps = {
	skills: Skill[];
	isSpecialistPromptInput: boolean;
	selectedAgentId?: string;
	enableSkillsInSpecialistPromptInput?: boolean;
	promptHasSkill: boolean;
	enableSkills?: boolean;
	siteId?: string;
	disableSkillTagHoverCardInEditor?: boolean;
	skillTagHoverCardMaxHeightInEditor?: number | string;
};

export const useSkillsManifest = ({
	skills,
	isSpecialistPromptInput,
	selectedAgentId,
	enableSkillsInSpecialistPromptInput = false,
	promptHasSkill,
	enableSkills = true,
	siteId,
	disableSkillTagHoverCardInEditor = false,
	skillTagHoverCardMaxHeightInEditor,
}: UseSkillsManifestProps): DefaultExtensionProvider<Skill>[] | undefined => {
	const isSearchMode = Boolean(useSearchMode());
	const chatSlashCommandsEnabled = isExperimentEnabled('platform_editor_slash_command');
	const isSkillEligible = useSlashCommandSkillEligibility({
		cloudId: siteId,
		enabled: chatSlashCommandsEnabled,
	});
	const stableIsHidden = useStableEventCallback((skill: Skill) => {
		if (isSearchMode || promptHasSkill) {
			return true;
		}
		if (
			!enableSkillsInSpecialistPromptInput &&
			isSpecialistAgentSelected(selectedAgentId, isSpecialistPromptInput)
		) {
			return true;
		}
		return !isSkillEligible(skill);
	});
	const { sendAnalyticsEvent } = useAnalytics();
	// Skill selection source is recorded on the AnalyticsPropertiesContext so the chat
	// input can attach it to the next `aiResult viewed` event without needing a
	// per-input store. The provider is mounted once per chat instance, scoping the value
	// to the input that consumes it.
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();
	const { fireSkillCreateFlowStart } = useCustomSkillsAnalytics();
	const quickInsertSkills = React.useMemo(() => {
		if (chatSlashCommandsEnabled) {
			// Stable registrations let item predicates control visibility as chat state changes.
			return skills;
		}
		if (
			isSearchMode ||
			(isSpecialistPromptInput && !enableSkillsInSpecialistPromptInput) ||
			promptHasSkill
		) {
			return EMPTY_SKILLS;
		}
		return skills;
	}, [
		chatSlashCommandsEnabled,
		skills,
		isSearchMode,
		isSpecialistPromptInput,
		enableSkillsInSpecialistPromptInput,
		promptHasSkill,
	]);
	const skillsBySlug = React.useMemo(
		() => new Map(skills.map((skill) => [skill.slug, skill])),
		[skills],
	);

	const createManifest = React.useCallback(
		(quickInsertSkills: Skill[]) => {
			if (chatSlashCommandsEnabled) {
				return buildSkillsManifest(quickInsertSkills, {
					isHidden: stableIsHidden,
					manifestTitle: 'Skills',
					categories: ['Skills'],
					// 1 is the default priority.
					priority: 99,
					onAction: async (skill) => {
						sendAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'rovoChatSkillSelected',
							attributes: {
								slashCommand: skill.slug,
								skillProductKey: skill.product,
								skillSelectionSource: 'editorTypeahead',
							},
						});
						if (skill.id === BUILD_SKILL_ARI && fg('fe_custom_skills_analytics')) {
							fireSkillCreateFlowStart('chatTypeAheadBuildSkill');
						}
						setAnalyticsPropertiesWithoutRerender({
							skillSelectionSource: 'editorTypeahead',
						});
						return createSkillNode(skill);
					},
					nodes: createSkillTagNodeRenderer(
						siteId,
						disableSkillTagHoverCardInEditor,
						skillTagHoverCardMaxHeightInEditor,
						skillsBySlug,
					),
				});
			}

			const manifest: ExtensionManifest<Skill> = {
				title: 'Skills',
				type: ROVO_SKILL_EXTENSION_TYPE,
				key: ROVO_SKILL_EXTENSION_KEY,
				categories: ['Skills'],
				icons: {
					'16': defaultIcon,
					'24': defaultIcon,
					'48': defaultIcon,
				},
				modules: {
					quickInsert: quickInsertSkills.map((skill) => {
						const { slug, description, helpText, name, product, iconName, tags, color } = skill;
						const lozenge = tags?.some((tag) => tag.toLowerCase() === 'beta')
							? adminSkillBetaLozenge
							: undefined;
						const title = isExperimentEnabled('display_skill_lozenge_in_editor')
							? `skill::${color ?? ''}::${slug}::${name}`
							: name;
						return {
							key: slug,
							keywords: ['skills', 'skill', slug, product ?? ''],
							// 1 is the default priority.
							priority: 99,
							title,
							description: truncateSkillDescription(
								resolveSkillDisplayDescription(helpText, description),
							),
							icon: getLegacySkillIcon(iconName),
							lozenge,
							action: async () => {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'rovoChatSkillSelected',
									attributes: {
										slashCommand: slug,
										skillProductKey: product,
										skillSelectionSource: 'editorTypeahead',
									},
								});
								if (skill.id === BUILD_SKILL_ARI && fg('fe_custom_skills_analytics')) {
									fireSkillCreateFlowStart('chatTypeAheadBuildSkill');
								}
								setAnalyticsPropertiesWithoutRerender({
									skillSelectionSource: 'editorTypeahead',
								});
								return createSkillNode(skill);
							},
						};
					}),
					nodes: createSkillTagNodeRenderer(
						siteId,
						disableSkillTagHoverCardInEditor,
						skillTagHoverCardMaxHeightInEditor,
						skillsBySlug,
					),
				},
			};

			return manifest;
		},
		[
			sendAnalyticsEvent,
			setAnalyticsPropertiesWithoutRerender,
			fireSkillCreateFlowStart,
			siteId,
			disableSkillTagHoverCardInEditor,
			skillTagHoverCardMaxHeightInEditor,
			skillsBySlug,
			chatSlashCommandsEnabled,
			stableIsHidden,
		],
	);

	const manifestProvider = React.useMemo(() => {
		// Return undefined if skills are disabled
		if (!enableSkills) {
			return undefined;
		}
		return [new DefaultExtensionProvider([createManifest(quickInsertSkills)])];
	}, [createManifest, enableSkills, quickInsertSkills]);

	return manifestProvider;
};
